import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../hooks/useI18n';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { profileAPI } from '../services/api';
import { 
  User, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertCircle,
  Save,
  Shield
} from 'lucide-react';

interface ProfileInfo {
  email: string;
  login: string;
  isAdmin: boolean;
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { t, isRTL } = useI18n();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [profileInfo, setProfileInfo] = useState<ProfileInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const fetchProfileInfo = async () => {
      try {
        const info = await profileAPI.getProfileInfo();
        setProfileInfo(info);
      } catch (error) {
        console.error('Failed to fetch profile info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileInfo();
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    setPasswordLoading(true);

    try {
      await profileAPI.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setMessage({ type: 'success', text: 'Password changed successfully! An email notification has been sent.' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to change password. Please try again.' 
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 spinner mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-secondary-50 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className={`transition-all duration-300 ${isRTL ? 'lg:mr-64 lg:ml-0' : 'lg:ml-64 lg:mr-0'}`}>
        <TopNav onSidebarToggle={() => setIsSidebarOpen(true)} />
        
        <main className="p-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-secondary-900 mb-2">{t('profile.title')}</h1>
              <p className="text-secondary-600">{t('profile.subtitle')}</p>
            </div>

            {/* Tabs */}
            <div className={`flex ${isRTL ? 'space-x-reverse' : 'space-x-1'} bg-white rounded-lg p-1 shadow-soft mb-6`}>
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md font-medium transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                }`}
              >
                <User className="w-5 h-5" />
                <span>{t('profile.profileInformation')}</span>
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md font-medium transition-colors ${
                  activeTab === 'security'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                }`}
              >
                <Shield className="w-5 h-5" />
                <span>{t('profile.security')}</span>
              </button>
            </div>

            {/* Message */}
            {message && (
              <div className={`mb-6 p-4 rounded-lg flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'} ${
                message.type === 'success' ? 'alert-success' : 'alert-error'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                )}
                <p>{message.text}</p>
              </div>
            )}

            {/* Profile Information Tab */}
            {activeTab === 'profile' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-xl font-semibold text-secondary-900">{t('profile.profileInformation')}</h2>
                  <p className="text-secondary-600">{t('profile.accountDetails')}</p>
                </div>
                <div className="card-body">
                  <div className="space-y-6">
                    <div className="profile-info-row">
                      <span className='profile-info-label'>{t('profile.email')}</span>
                      <span className="profile-info-value">{profileInfo?.email}</span>
                    </div>
                    <div className="profile-info-row">
                      <span className='profile-info-label'>{t('profile.username')}</span>
                      <span className="profile-info-value">{profileInfo?.login}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-xl font-semibold text-secondary-900">{t('profile.changePassword')}</h2>
                  <p className="text-secondary-600">{t('profile.updatePasswordDescription')}</p>
                </div>
                <div className="card-body">
                  <form onSubmit={handlePasswordSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="currentPassword" className="form-label">
                        {t('profile.currentPassword')}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-secondary-400" />
                        </div>
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          id="currentPassword"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          placeholder={t('profile.enterCurrentPassword')}
                          className="form-input pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                          ) : (
                            <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="newPassword" className="form-label">
                        {t('profile.newPassword')}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-secondary-400" />
                        </div>
                        <input
                          type={showNewPassword ? "text" : "password"}
                          id="newPassword"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          placeholder={t('profile.enterNewPassword')}
                          className="form-input pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                          ) : (
                            <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                          )}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-secondary-500">
                        {t('profile.passwordRequirements')}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="form-label">
                        {t('profile.confirmNewPassword')}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-secondary-400" />
                        </div>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          placeholder={t('profile.confirmNewPasswordPlaceholder')}
                          className={`form-input pl-10 pr-10 ${
                            passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword 
                              ? 'border-error-500 focus:ring-error-500' 
                              : ''
                          }`}
                          required
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                          ) : (
                            <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                          )}
                        </button>
                      </div>
                      {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <p className='mt-1 text-sm text-error-600'>{t('profile.passwordsDoNotMatch')}</p>
                      )}
                    </div>

                    <div className="flex justify-end pt-4 border-t border-secondary-200">
                      <button
                        type="submit"
                        className="btn-primary flex items-center space-x-2"
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? (
                          <>
                            <div className="w-4 h-4 spinner"></div>
                            <span>{t('profile.updating')}</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>{t('profile.updatePassword')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
